import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/uk';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import { CancelledOrdersGroupBy, DatePresetKey, ICancelledOrdersSeriesItem } from '@/models/cancelledOrders.model';
import { assertUnreachable } from '@/utils';

export const resolveGroupByForRange = (
  preset: DatePresetKey,
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): CancelledOrdersGroupBy => {
  switch (preset) {
    case 'last_7_days':
    case 'last_30_days':
    case 'current_month':
    case 'last_month':
      return 'day';
    case 'current_year':
    case 'previous_year':
    case 'all_time':
      return 'month';
    case 'custom': {
      if (!dateFrom || !dateTo) return 'day';
      const days = dateTo.startOf('day').diff(dateFrom.startOf('day'), 'day') + 1;
      if (days <= 31) return 'day';
      if (days <= 93) return 'week';
      return 'month';
    }
    default:
      return assertUnreachable(preset);
  }
};

const bucketKey = (date: string, groupBy: CancelledOrdersGroupBy): string => {
  const parsed = dayjs(date);

  switch (groupBy) {
    case 'day':
      return parsed.format('YYYY-MM-DD');
    case 'week':
      return parsed.startOf('week').format('YYYY-MM-DD');
    case 'month':
      return parsed.startOf('month').format('YYYY-MM-DD');
    default:
      return assertUnreachable(groupBy);
  }
};

export const aggregateCancelledSeries = (
  series: ICancelledOrdersSeriesItem[],
  groupBy: CancelledOrdersGroupBy,
): ICancelledOrdersSeriesItem[] => {
  if (groupBy === 'day') {
    return series;
  }

  const buckets = new Map<
    string,
    {
      confirmed_amount: number;
      cancelled_amount: number;
      confirmed_count: number;
      cancelled_count: number;
    }
  >();

  series.forEach((item) => {
    const key = bucketKey(item.date, groupBy);
    const current = buckets.get(key) ?? {
      confirmed_amount: 0,
      cancelled_amount: 0,
      confirmed_count: 0,
      cancelled_count: 0,
    };

    current.confirmed_amount += item.confirmed_amount;
    current.cancelled_amount += item.cancelled_amount;
    current.confirmed_count += item.confirmed_count;
    current.cancelled_count += item.cancelled_count;
    buckets.set(key, current);
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, bucket]) => {
      const total = bucket.confirmed_count + bucket.cancelled_count;
      return {
        date,
        confirmed_amount: Number(bucket.confirmed_amount.toFixed(2)),
        cancelled_amount: Number(bucket.cancelled_amount.toFixed(2)),
        confirmed_count: bucket.confirmed_count,
        cancelled_count: bucket.cancelled_count,
        confirmed_share_percent: total ? Number(((bucket.confirmed_count / total) * 100).toFixed(1)) : 0,
        cancelled_share_percent: total ? Number(((bucket.cancelled_count / total) * 100).toFixed(1)) : 0,
      };
    });
};

export const formatChartAxisTickWithSpan = (
  date: string,
  groupBy: CancelledOrdersGroupBy,
  series: { date: string }[],
  locale = 'uk',
): string => {
  const parsed = dayjs(date);
  const dayjsLocale = locale.startsWith('en') ? 'en' : locale.startsWith('ru') ? 'ru' : 'uk';

  if (groupBy === 'month') {
    return parsed.locale(dayjsLocale).format('MMM YYYY');
  }

  if (series.length >= 2) {
    const first = dayjs(series[0].date);
    const last = dayjs(series[series.length - 1].date);
    if (last.year() !== first.year()) {
      return parsed.format('DD.MM.YYYY');
    }
  }

  return parsed.format('DD.MM');
};
